/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-09-11 13:51:40
 *
 *******************************************************/

import _ from 'lodash';
import React, { Component } from 'react';
import Classnames from 'classnames';
import Select from 'react-select';
import { connect } from 'react-redux';
import { Icon, Spinner, TextField, TextStyle } from '@shopify/polaris';
import { SearchMinor, SearchMajor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import TemplateItem from 'src/components/views/theme-settings/preview/page-templates/template-item';

import * as ThemeSettingAction from 'src/stores/actions/theme-settings-actions';
import { selectStyle } from 'src/components/views/common/forms/render-select.style';
import { getTemplateImage } from 'src/constants/theme-settings';
import {
  DropdownIndicator,
  styles
} from 'src/components/views/theme-settings/form/react-select-style';
import { SORT_TEMPLATES_OPTIONS, MAPPING_THEME_NAME } from 'src/constants/theme-settings.js';

type Props = {};

type ComposedProps = Props & WithI18nProps;

const SORT_VALUE = {
  ASCENDING: 'asc',
  DESCENDING: 'desc'
};

class PageTemplates extends Component<ComposedProps> {
  state = {
    search: '',
    sort: SORT_VALUE.ASCENDING
  };

  componentDidMount() {
    const { getListThemeDefaultValue, listThemeTemplate } = this.props;

    setTimeout(() => {
      this.scrollToCurrentTemplate();
    }, 150);

    if (_.isEmpty(listThemeTemplate)) {
      getListThemeDefaultValue();
    }
  }

  componentWillReceiveProps(nextProps) {
    const { listThemeTemplate } = this.props;
    const { listThemeTemplate: nextListThemeTemplate } = nextProps;
    if (_.isEmpty(listThemeTemplate) && !_.isEmpty(nextListThemeTemplate)) {
      this.preloadAllThemeImages(nextListThemeTemplate);
      setTimeout(() => {
        this.scrollToCurrentTemplate();
      }, 150);
    }
  }

  scrollToCurrentTemplate = props => {
    const { themeLibTemplate, boostThemeLib } = props || this.props;
    const templateName = themeLibTemplate || boostThemeLib;
    const dom = document.getElementById(`template-item-${MAPPING_THEME_NAME[templateName]}`);
    if (dom) {
      dom.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Preload for browser's caching images
  preloadAllThemeImages = listThemeTemplate => {
    listThemeTemplate.forEach(template => {
      const templateImage = getTemplateImage(template?.themeName);
      if (templateImage?.desktop) {
        this.preloadImage(templateImage?.desktop);
      }
    });
  };

  preloadImage = imgUrl => {
    let img = new Image();
    img.src = imgUrl;
  };

  handleSubmit = e => {
    e.preventDefault();
  };

  handleChangeSort = sortOption => {
    this.setState({
      sort: sortOption.value
    });
  };

  handleChangeSearch = value => {
    this.setState({
      search: value
    });
  };

  handleClearButton = () => {
    this.setState({
      search: ''
    });
  };

  render() {
    const {
      i18n,
      listThemeTemplate,
      listThemeTemplateLoading,
      onSelectTemplate,
      onViewDemoTemplate,
      themeLibTemplate,
      boostThemeLib
    } = this.props;
    const { search, sort } = this.state;
    const sortOptions = SORT_TEMPLATES_OPTIONS.map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    if (listThemeTemplateLoading) {
      return (
        <div className="BC-theme-setting-page-templates__loading">
          <Spinner accessibilityLabel="Loading" size="large" />
        </div>
      );
    }

    const renderListTemplate = listThemeTemplate?.filter(theme => {
      const themeName = MAPPING_THEME_NAME[theme.themeName];
      return themeName.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase());
    });

    renderListTemplate.sort((a, b) => {
      if (sort === SORT_VALUE.ASCENDING) {
        return a.themeName.trim().localeCompare(b.themeName.trim());
      } else {
        return b.themeName.trim().localeCompare(a.themeName.trim());
      }
    });

    const isChrome = /chrome/i.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isFirefoxMacOS =
      navigator.userAgent.includes('Firefox') && navigator.userAgent.includes('Mac OS');
    return (
      <div className="BC-theme-setting-page-templates">
        <h2 className="BC-theme-setting-page-templates__title">
          {i18n.translate('AdminHTML.ThemeSetting.LayoutModal.menuItem_2')}
        </h2>
        <p className="BC-theme-setting-page-templates__desc">
          {i18n.translate('AdminHTML.ThemeSetting.LayoutModal.pageTemplates_Description')}
        </p>

        <div className="BC-theme-setting-page-templates__search">
          <form onSubmit={this.handleSubmit}>
            <TextField
              clearButton
              onClearButtonClick={this.handleClearButton}
              onChange={this.handleChangeSearch}
              value={search}
              prefix={<Icon source={SearchMinor} color="base" />}
              placeholder={i18n.translate(
                'AdminHTML.ThemeSetting.LayoutModal.pageTemplates_Search'
              )}
            />
          </form>
        </div>

        <div className="BC-theme-setting-page-templates__content">
          <div className="BC-theme-setting-page-templates__content-header">
            <p>
              {renderListTemplate?.length > 0
                ? `${renderListTemplate?.length} ${i18n.translate(
                    `AdminHTML.ThemeSetting.Common.${
                      renderListTemplate?.length > 1 ? 'templates' : 'template'
                    }`
                  )}`
                : i18n.translate(`AdminHTML.ThemeSetting.LayoutModal.noTemplateNumber`, {
                    t1: renderListTemplate?.length,
                    t2: listThemeTemplate?.length
                  })}
            </p>

            <Select
              options={sortOptions}
              value={
                sortOptions.find(item => {
                  return item.value === sort;
                }) || null
              }
              onChange={this.handleChangeSort}
              styles={{ ...selectStyle, ...styles }}
              maxMenuHeight={140}
              classNamePrefix="BC-Select"
              noOptionsMessage={() =>
                i18n.translate('AdminHTML.Common.Empty.reactSelect_NoOptions')
              }
              isSearchable={false}
              menuPlacement="auto"
              menuPosition="absolute"
              blurInputOnSelect
              components={{ DropdownIndicator }}
            />
          </div>

          <div
            className={Classnames('BC-theme-setting-page-templates__content-body', {
              chrome: isSafari || isChrome,
              'firefox-mac': isFirefoxMacOS
            })}>
            {renderListTemplate.map(item => (
              <TemplateItem
                theme={item}
                key={item.id}
                onSelect={onSelectTemplate}
                onViewDemo={onViewDemoTemplate}
                themeLibTemplate={themeLibTemplate}
                boostThemeLib={boostThemeLib}
              />
            ))}
            {!renderListTemplate.length && (
              <div className="BC-theme-setting-page-templates__no-result">
                <Icon source={SearchMajor} color="base" />
                <p className="title">
                  {i18n.translate(`AdminHTML.ThemeSetting.LayoutModal.noTemplateFound`)}
                </p>
                <TextStyle variation="subdued">
                  {i18n.translate(`AdminHTML.ThemeSetting.LayoutModal.trySearchTemplateAgain`)}
                </TextStyle>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateThemeSetting: setting =>
    dispatch(ThemeSettingAction.updateThemeSetting(setting, ownProps.i18n)),
  getThemeSettingById: id => dispatch(ThemeSettingAction.getThemeSettingById(id, ownProps.i18n)),
  getThemeSettingDefaultValueById: id =>
    dispatch(ThemeSettingAction.getThemeSettingDefaultValueById(id, ownProps.i18n)),
  updateFieldReducer: (fieldName, data) =>
    dispatch(ThemeSettingAction.handleUpdateField(fieldName, data)),
  updateMultiFieldsReducer: data => dispatch(ThemeSettingAction.handleUpdateMultiFields(data)),

  getListThemeDefaultValue: () =>
    dispatch(ThemeSettingAction.getListThemeDefaultValue(ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});

  return {
    tenantApp,
    themeName: _.get(state, 'themeSetting.themeName', ''),
    listThemeTemplate: _.get(state, 'themeSetting.listThemeTemplate', []),
    listThemeTemplateLoading: _.get(state, 'themeSetting.listThemeTemplateLoading', false),
    listThemeTemplateError: _.get(state, 'themeSetting.listThemeTemplateError', null),
    themeSettingDefaultValue: _.get(state, 'themeSetting.themeSettingDefaultValue', {}),
    themeSettingDefaultThemeValue: _.get(state, 'themeSetting.themeSettingDefaultThemeValue', {}),
    isFormReplaceDirty: _.get(state, 'themeSetting.isFormReplaceDirty', false),
    themeLibTemplate: _.get(state, 'themeSetting.themeLibTemplate', ''),
    boostThemeLib: _.get(state, 'themeSetting.boostThemeLib', 'default')
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(PageTemplates));
